import { useEffect, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { EnrichmentRunState, type EnrichmentRunRead, type EnrichmentRunScope } from '@signalvote/shared';
import { authService } from '../../services/authService';
import { apiJson, apiResponse, type ApiErrorBody } from './apiFetch';
import { queryKeys } from '../../lib/queryKeys';
import { readSseEvents } from '../../lib/fundraising/prospectListUpload';
import { NEVER_RUN } from '../../lib/fundraising/enrichmentWork';
import { useCampaignStore } from '../../stores';

const RUN_PATH = '/api/fundraising/enrichment/run';
// Fast enough that the step card visibly walks through the matcher's stages; the status read is in memory.
const POLL_WHILE_RUNNING_MS = 1_500;

/**
 * The campaign's latest "match donors to federal giving" run. While one is in progress it follows the
 * run's event stream, so the AI judge's answers land as they happen; the poll is the fallback whenever
 * the stream isn't open. When a run finishes it refreshes the enriched people, whose federal giving the
 * run just rewrote.
 */
export function useEnrichmentRun() {
  const { selectedCampaign } = useCampaignStore();
  const queryClient = useQueryClient();
  const [streaming, setStreaming] = useState(false);
  const [reconnect, setReconnect] = useState(0);
  const query = useQuery({
    queryKey: queryKeys.fundraising.enrichmentRun(),
    queryFn: () => apiJson<EnrichmentRunRead>(RUN_PATH, { error: 'Failed to fetch the enrichment run' }),
    enabled: authService.isAuthenticated() && !!selectedCampaign,
    refetchInterval: (q) =>
      !streaming && q.state.data?.state === EnrichmentRunState.Running ? POLL_WHILE_RUNNING_MS : false,
  });

  const state = query.data?.state;

  useEffect(() => {
    if (state !== EnrichmentRunState.Running) return;
    const key = queryKeys.fundraising.enrichmentRun();
    const controller = new AbortController();
    let retry: ReturnType<typeof setTimeout> | undefined;

    void (async () => {
      try {
        const response = await apiResponse(`${RUN_PATH}/stream`, { signal: controller.signal });
        if (!response.ok || !response.body) return;
        setStreaming(true);
        const reader = response.body.getReader();
        const decoder = new TextDecoder();
        let buffer = '';
        let following = false;
        while (true) {
          const { done, value } = await reader.read();
          if (done) break;
          const { events, rest } = readSseEvents<EnrichmentRunRead>(buffer + decoder.decode(value, { stream: true }));
          buffer = rest;
          for (const run of events) {
            // A stream opened before the server has the run (the optimistic start) first reads the last
            // one, finished; writing it would flip the card back. Follow only once it reads as running.
            following ||= run.state === EnrichmentRunState.Running;
            if (following) queryClient.setQueryData(key, run);
          }
        }
      } catch {
        // Aborted on unmount, or the stream dropped: the poll takes over.
      } finally {
        if (!controller.signal.aborted) {
          setStreaming(false);
          // Still running as far as the page knows: try the stream again after a poll's wait.
          if (queryClient.getQueryData<EnrichmentRunRead>(key)?.state === EnrichmentRunState.Running) {
            retry = setTimeout(() => setReconnect((n) => n + 1), POLL_WHILE_RUNNING_MS);
          }
        }
      }
    })();

    return () => {
      controller.abort();
      clearTimeout(retry);
      setStreaming(false);
    };
  }, [state, reconnect, queryClient]);
  const previous = useRef(state);
  useEffect(() => {
    if (previous.current === EnrichmentRunState.Running && state !== EnrichmentRunState.Running) {
      // The book's match statuses and Asks come from the run.
      void queryClient.invalidateQueries({ queryKey: queryKeys.fundraising.prospects() });
    }
    previous.current = state;
  }, [state, queryClient]);

  return query;
}

/**
 * Start a run over the scope's people: everyone in the book, or only those no run has looked at. While
 * one is already going the API starts nothing and returns that one, so the page follows it. Optimistic: the run reads as
 * started the moment it's asked, so the pipeline's timer starts on the click, not a round trip later; a
 * refusal puts the last run back.
 */
export function useStartEnrichmentRun() {
  const queryClient = useQueryClient();
  const key = queryKeys.fundraising.enrichmentRun();
  return useMutation({
    mutationFn: (scope: EnrichmentRunScope) =>
      apiJson<EnrichmentRunRead>(`${RUN_PATH}?scope=${scope}`, {
        method: 'POST',
        // A JSON `null` body reaches here as null, so keep the optional chain.
        error: (body: ApiErrorBody | null) => new Error(body?.error ?? body?.message ?? 'Could not start matching donors. Try again.'),
      }),
    onMutate: async () => {
      await queryClient.cancelQueries({ queryKey: key });
      const previous = queryClient.getQueryData<EnrichmentRunRead>(key);
      queryClient.setQueryData<EnrichmentRunRead>(key, {
        ...NEVER_RUN,
        state: EnrichmentRunState.Running,
        startedAt: new Date().toISOString(),
      });
      return { previous };
    },
    onError: (_error, _vars, context) => queryClient.setQueryData(key, context?.previous),
    onSuccess: (run) => queryClient.setQueryData(key, run),
    onSettled: () => queryClient.invalidateQueries({ queryKey: key }),
  });
}
